import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';

import Avatar from '@atlaskit/avatar/Avatar';
import { Flex, xcss } from '@atlaskit/primitives';

import { ProfileCardWrapper } from '@confluence/profile';

import { i18n } from './i18n';
import type { UserShape } from './types';

const contentStyles = xcss({
	color: 'color.text.subtle',
	fontWeight: 'font.weight.regular',
	borderRadius: 'border.radius',

	paddingTop: 'space.025',
	paddingBottom: 'space.025',
	paddingLeft: 'space.025',
	paddingRight: 'space.050',

	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
		cursor: 'pointer',
	},
	':active': {
		backgroundColor: 'color.background.neutral.subtle.pressed',
	},
});

type PageOwnershipBylineContentProps = {
	owner: UserShape | null;
	author: UserShape;
	testId?: string;
};

export const BadgeStylePageOwnershipByline = ({
	owner,
	author,
	testId,
}: PageOwnershipBylineContentProps) => {
	const intl = useIntl();

	const pageOwner = owner ?? author;
	const avatarSrc = pageOwner.isAnonymous || !pageOwner.avatarUrl ? undefined : pageOwner.avatarUrl;

	return (
		<ProfileCardWrapper
			trigger="click"
			userId={pageOwner.userId}
			ariaLabel={intl.formatMessage(i18n.abbreviatedBylineMainText, {
				user: pageOwner.fullName,
			})}
		>
			<Flex
				role="button"
				aria-haspopup
				justifyContent="center"
				alignItems="center"
				gap="space.050"
				xcss={contentStyles}
				testId={testId}
			>
				<Avatar src={avatarSrc} size="xsmall" borderColor="transparent" />
				<FormattedMessage
					{...i18n.abbreviatedBylineMainText}
					values={{ user: pageOwner.fullName }}
				/>
			</Flex>
		</ProfileCardWrapper>
	);
};
